// Port of webapp/src/boardUtils.ts — grouping cards into kanban/table groups.
// Kept deliberately close to the original implementation.

import {BoardGroup, IPropertyOption, IPropertyTemplate} from '../types/board'
import {Card} from '../types/card'

function groupCardsByOptions(cards: Card[], optionIds: string[], groupByProperty?: IPropertyTemplate): BoardGroup[] {
    const groups: BoardGroup[] = []
    for (const optionId of optionIds) {
        if (optionId) {
            const option = groupByProperty?.options.find((o) => o.id === optionId)
            if (option) {
                groups.push({
                    option,
                    cards: cards.filter((o) => optionId === o.fields?.properties[groupByProperty!.id]),
                })
            }
            // an unknown optionId is a deleted option; ignore it like upstream
        } else {
            // the empty group: cards whose value is missing or no longer an option
            const emptyGroupCards = cards.filter((card) => {
                const groupByOptionId = card.fields.properties[groupByProperty?.id || '']
                return !groupByOptionId || !groupByProperty?.options.find((option) => option.id === groupByOptionId)
            })
            groups.push({
                option: {id: '', value: `未设置${groupByProperty?.name}`, color: ''} as IPropertyOption,
                cards: emptyGroupCards,
            })
        }
    }
    return groups
}

function getOptionGroups(cards: Card[], visibleOptionIds: string[], hiddenOptionIds: string[], groupByProperty?: IPropertyTemplate): {visible: BoardGroup[], hidden: BoardGroup[]} {
    let unassignedOptionIds: string[] = []
    if (groupByProperty) {
        unassignedOptionIds = groupByProperty.options.
            filter((o: IPropertyOption) => !visibleOptionIds.includes(o.id) && !hiddenOptionIds.includes(o.id)).
            map((o: IPropertyOption) => o.id)
    }
    const allVisibleOptionIds = [...visibleOptionIds, ...unassignedOptionIds]

    // If the empty group position is not explicitly specified, make it the first visible column
    if (!allVisibleOptionIds.includes('') && !hiddenOptionIds.includes('')) {
        allVisibleOptionIds.unshift('')
    }

    return {
        visible: groupCardsByOptions(cards, allVisibleOptionIds, groupByProperty),
        hidden: groupCardsByOptions(cards, hiddenOptionIds, groupByProperty),
    }
}

function getPersonGroups(cards: Card[], groupByProperty: IPropertyTemplate, hiddenOptionIds: string[]): {visible: BoardGroup[], hidden: BoardGroup[]} {
    const grouped = cards.reduce((unique: Record<string, Card[]>, item: Card): Record<string, Card[]> => {
        let key = item.fields.properties[groupByProperty.id] as string
        if (groupByProperty.type === 'createdBy') {
            key = item.createdBy
        } else if (groupByProperty.type === 'updatedBy') {
            key = item.modifiedBy
        }
        const curGroup = unique[key || ''] ?? []
        return {...unique, [key || '']: [...curGroup, item]}
    }, {})

    const hiddenGroups: BoardGroup[] = []
    const visibleGroups: BoardGroup[] = []
    Object.entries(grouped).forEach(([key, value]) => {
        const propertyOption = {id: key, value: key, color: ''} as IPropertyOption
        if (hiddenOptionIds.includes(key)) {
            hiddenGroups.push({option: propertyOption, cards: value})
        } else {
            visibleGroups.push({option: propertyOption, cards: value})
        }
    })

    return {visible: visibleGroups, hidden: hiddenGroups}
}

export function getVisibleAndHiddenGroups(cards: Card[], visibleOptionIds: string[], hiddenOptionIds: string[], groupByProperty?: IPropertyTemplate): {visible: BoardGroup[], hidden: BoardGroup[]} {
    if (groupByProperty?.type === 'createdBy' || groupByProperty?.type === 'updatedBy' || groupByProperty?.type === 'person') {
        return getPersonGroups(cards, groupByProperty, hiddenOptionIds)
    }
    return getOptionGroups(cards, visibleOptionIds, hiddenOptionIds, groupByProperty)
}

export {groupCardsByOptions}
